import App from 'next/app';
import React from 'react';
import { withRouter } from 'next/router';
import { ApolloProvider } from '@apollo/client';
import { RawIntlProvider, createIntl } from 'react-intl';
import Theme from '@fuww/components/src/Theme';
import { NavigationProvider } from '@fuww/components/src/lib/useShowNavigation';
import Layout from '../components/Layout';
import CookieConsent from '../components/CookieConsent';
import PlausibleScript from '../components/PlausibleScript';
import SiteContext from '../components/SiteContext';
import initApollo from '../lib/initApollo';
import hasNews from '../lib/hasNews';
import hasLocalNews from '../lib/hasLocalNews';
import isJobsComplete from '../lib/isJobsComplete';
import hasJobLanding from '../lib/hasJobLanding';
import hasJobsInLanding from '../lib/hasJobsInLanding';
import isLite from '../lib/isLite';
import getPrefix from '../lib/getPrefix';
import getAdUnitPrefix from '../lib/getAdUnitPrefix';
import getNewsboard from '../lib/getNewsboard';
import getJobsLocale from '../lib/getJobsLocale';
import getOrigin from '../lib/getOrigin';
import getBoard from '../lib/getBoard';
import isProduction from '../lib/isProduction';
import cache from '../lib/intlCache';
import { getMessages } from '../lib/i18n';
import getAppProperties from '../lib/getAppProperties';
import removeTrailingDot from '../lib/removeTrailingDot';
import getWindowUrl from '../lib/getWindowUrl';
import correctPrefix from '../lib/correctPrefix';
import redirectAmp from '../lib/redirectAmp';
import getSeed from '../lib/getSeed';
import { SeedContext } from '../lib/useSeed';
import '../lib/reload';

const cacheControl = 'max-age=600, '
  + 'stale-while-revalidate=3600, stale-if-error=259200';

class MyApp extends App {
  static async getInitialProps({ Component, ctx: context }) {
    const pageProperties = Component.getInitialProps
      ? await Component.getInitialProps(context)
      : {};

    const { res, req } = context;

    if (!req || req.get) correctPrefix(context);

    redirectAmp(context);

    if (res && res.get && !res.headersSent) {
      if (!res.get('cache-control')) {
        res.setHeader('Cache-Control', cacheControl);
      }

      if (!res.hasHeader('Surrogate-Key') && !res.hasHeader('Cache-Tag')) {
        res.setHeader('Surrogate-Key', 'pwa-ssr');
        res.setHeader('Cache-Tag', 'pwa-ssr');
      }
    }

    const appProperties = getAppProperties(context);
    const { locale } = appProperties;

    return {
      ...(process.browser
        // eslint-disable-next-line no-underscore-dangle
        ? { messages: window.__NEXT_DATA__.props.messages }
        : { messages: await getMessages(locale) }
      ),
      ...appProperties,
      pageProps: pageProperties,
      seed: getSeed(),
    };
  }

  componentDidMount() {
    removeTrailingDot();
  }

  render() {
    const {
      Component,
      pageProps: pageProperties = {},
      locale = pageProperties.locale || 'en-IE',
      messages,
      router: { route },
      seed,
    } = this.props;
    const { apolloClient, apolloState } = pageProperties;
    const url = getWindowUrl() || this.props.url || pageProperties.url;
    const client = apolloClient
      || initApollo(apolloState, { reqUrl: url });
    const getLayout = Component.getLayout
      || ((page) => <Layout>{page}</Layout>);
    const prefix = getPrefix(url);

    const intl = createIntl(
      {
        locale,
        messages,
      },
      cache,
    );

    return (
      <>
        <SiteContext.Provider value={{
          url,
          prefix,
          locale,
          jobsLocale: getJobsLocale(locale),
          hasLocalNews: hasLocalNews(prefix),
          hasNews: hasNews(prefix),
          lite: isLite(prefix),
          jobsComplete: isJobsComplete(prefix),
          jobsLanding: hasJobLanding(prefix),
          hasJobsInLanding: hasJobsInLanding(prefix),
          adUnitPrefix: getAdUnitPrefix(url),
          production: isProduction(url),
          newsboard: getNewsboard(prefix),
          board: getBoard(prefix),
          origin: getOrigin(url),
        }}
        >
          <SeedContext.Provider value={seed}>
            <NavigationProvider>
              <ApolloProvider client={client}>
                <RawIntlProvider
                  value={intl}
                >
                  <Theme>
                    {getLayout(<Component {...pageProperties} />)}
                    {route === '/appShell' ? null : <CookieConsent />}
                  </Theme>
                </RawIntlProvider>
              </ApolloProvider>
            </NavigationProvider>
          </SeedContext.Provider>
        </SiteContext.Provider>
        <PlausibleScript />
      </>
    );
  }
}

export default withRouter(MyApp);
